import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

type Stage = 'fax' | 'chunks' | 'answer' | 'source';
const STAGES: Stage[] = ['fax', 'chunks', 'answer', 'source'];
const STAGE_MS = 3_200;

/**
 * Hero illustration that cycles through the pipeline:
 *   FAX  →  CHUNKS  →  KEIGO ANSWER  →  SOURCE VIEWER
 *
 * Each stage is rendered as a paper card; the active stage glides in
 * from the right and the previous slides out to the left. A progress
 * dot row at the bottom shows position in the loop. Pauses on hover
 * so a designer can pin the demo to a specific frame for review.
 *
 * All copy is i18n'd (default values are JP-aware) so localized landing
 * pages stay coherent.
 */
export function HeroMorph() {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % STAGES.length), STAGE_MS);
    return () => clearInterval(id);
  }, [paused]);

  const stage = STAGES[idx];

  return (
    <div
      className="relative w-full aspect-[5/6] max-w-lg ml-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Indigo 知 hanko behind — anchors the brand even between frame swaps */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="absolute top-6 right-6 h-24 w-24 rounded-full flex items-center justify-center bg-accent-500 text-white shadow-press rotate-[8deg]"
      >
        <span className="font-serif text-4xl">知</span>
      </motion.div>

      {/* Stage progress dots */}
      <div className="absolute bottom-2 left-2 flex gap-1.5">
        {STAGES.map((s, i) => (
          <button
            key={s}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={t(`hero.stage.${s}`, { defaultValue: s })}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? 'w-7 bg-accent-500' : 'w-1.5 bg-ink-300 hover:bg-ink-400'
            }`}
          />
        ))}
      </div>

      {/* Stage label */}
      <div className="absolute top-3 left-3 flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] text-accent-600 font-semibold">
        <span className="h-1.5 w-1.5 rounded-full bg-citation-500 animate-pulse" />
        <span>{t(`hero.stage_label.${stage}`, { defaultValue: stageLabel(stage) })}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={stage}
          initial={{ opacity: 0, x: 60, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -60, scale: 0.96 }}
          transition={{ duration: 0.55, ease: [0.16, 0.84, 0.44, 1] }}
          className="absolute inset-x-2 top-12 bottom-10"
        >
          <StageCard stage={stage} t={t} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function stageLabel(stage: Stage): string {
  switch (stage) {
    case 'fax': return 'INGEST · fax PDF';
    case 'chunks': return 'PARSE · chunks';
    case 'answer': return 'ANSWER · keigo';
    case 'source': return 'CITE · source viewer';
  }
}

function StageCard({ stage, t }: { stage: Stage; t: (k: string, opts?: Record<string, unknown>) => string }) {
  if (stage === 'fax') {
    return (
      <div className="card-paper p-5 h-full flex flex-col gap-3">
        <div className="flex items-center justify-between text-[11px]">
          <span className="num text-ink-500">議事録_Q3.fax.pdf</span>
          <span className="rounded-md bg-warn-500/10 text-warn-700 border border-warn-500/30 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold">FAX</span>
        </div>
        <div className="flex-1 rounded-lg bg-paper-100 border border-dashed border-ink-200 p-4 grid place-items-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_5px,rgba(0,0,0,0.04)_5px,rgba(0,0,0,0.04)_6px)] opacity-60" />
          <div className="absolute top-3 right-3 h-10 w-10 rounded-full border-2 border-citation-500 text-citation-500 text-[10px] font-bold flex items-center justify-center rotate-[14deg]" style={{ fontFamily: '"Noto Serif JP",serif' }}>
            山田
          </div>
          <div className="relative text-center space-y-1">
            <div className="font-serif text-[14px] text-ink-800">議事録 — Q3 役員会</div>
            <div className="num text-[11px] text-ink-500">2026年7月23日 14:00–15:30</div>
            <div className="num text-[10.5px] text-ink-400 mt-3">出席者 6名 · 議題 5件 · 決定 4件</div>
          </div>
        </div>
        <div className="text-[11.5px] text-ink-500">
          {t('hero.morph.fax_caption', { defaultValue: 'Faxed minutes with hanko + handwritten margin notes — typical JP enterprise input.' })}
        </div>
      </div>
    );
  }

  if (stage === 'chunks') {
    const chunks = [
      { id: 'c-1242', heading: '/議題 1 / ベンダーA社', text: 'API遅延が継続。代替評価を次四半期…' },
      { id: 'c-1287', heading: '/決定事項', text: '次回までに見直し案を提出 (山田)' },
      { id: 'c-1301', heading: '/次回', text: '2026年8月14日 同会議室' },
    ];
    return (
      <div className="card-paper p-5 h-full flex flex-col gap-2">
        <div className="text-[11px] num text-ink-500 mb-1">3 / 12 chunks · text-embedding-3-large</div>
        {chunks.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.35 }}
            className="rounded-lg border border-ink-100 bg-paper-50 px-3 py-2"
          >
            <div className="text-[10.5px] num text-accent-600 font-semibold">{c.heading}</div>
            <div className="text-[12.5px] text-ink-800 mt-0.5 leading-snug">{c.text}</div>
            <div className="num text-[10px] text-ink-400 mt-1">{c.id}</div>
          </motion.div>
        ))}
      </div>
    );
  }

  if (stage === 'answer') {
    return (
      <div className="card-paper p-5 h-full flex flex-col gap-3">
        <div className="text-[11px] text-ink-500 num">Knowlez AI · 412 tok · $0.0021</div>
        <div className="rounded-lg bg-paper-100 px-3 py-2 text-[12.5px] text-ink-800">
          CAC の 推移 を 3 行 で 要約 して ください。
        </div>
        <div className="relative text-[13px] text-ink-900 leading-relaxed flex-1">
          <p>
            ¥48,200 → ¥41,900 → ¥36,400（直近3四半期）。<span className="chip-citation text-[10px]">chunk:2118</span>
            <br />
            ペイドチャネル比率が60%→42%へ低下。<span className="chip-citation text-[10px]">chunk:2204</span>
            <br />
            紹介プログラム拡大が主因。<span className="chip-citation text-[10px]">chunk:2231</span>
          </p>
        </div>
        <div className="text-[11px] text-ink-500 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {t('hero.morph.cited', { defaultValue: '3 chunks cited · click any chip to open the source' })}
        </div>
      </div>
    );
  }

  // source viewer
  return (
    <div className="card-paper p-5 h-full flex flex-col gap-3">
      <div className="flex items-center justify-between text-[11px]">
        <span className="num text-ink-500">FY26-Q1-board.pdf · p. 4</span>
        <span className="num text-[10px] text-citation-700 bg-citation-50 border border-citation-200 rounded-md px-1.5 py-0.5 uppercase tracking-[0.14em] font-semibold">chunk:2118</span>
      </div>
      <div className="flex-1 rounded-lg bg-paper-100 border border-ink-100 px-4 py-3 text-[12.5px] text-ink-800 leading-relaxed relative overflow-hidden">
        <div className="absolute top-2 right-2 num text-[10px] text-ink-400">/Finance / CAC</div>
        <p className="mt-2">
          Customer Acquisition Cost (CAC) declined from{' '}
          <mark className="bg-citation-100 text-citation-700 rounded px-0.5">¥48,200</mark> in Q3 FY25 to{' '}
          <mark className="bg-citation-100 text-citation-700 rounded px-0.5">¥41,900</mark> in Q4 FY25 and{' '}
          <mark className="bg-citation-100 text-citation-700 rounded px-0.5">¥36,400</mark> in Q1 FY26, a cumulative reduction of 24.5%.
        </p>
        <p className="mt-2 text-ink-500 text-[11.5px]">
          {t('hero.morph.source_meta', { defaultValue: 'Drive · finance@acme.co.jp · last synced 3m ago · ACL re-checked' })}
        </p>
      </div>
      <div className="text-[11px] text-ink-500">
        {t('hero.morph.source_caption', { defaultValue: 'Every citation deep-links to the exact paragraph, with the source-system ACL still attached.' })}
      </div>
    </div>
  );
}
